import { z } from "zod";

import { DESCRIPTION_MAX_LENGTH, NO_CLIENT } from "@/features/projects/lib/pickerOptions";
import type { ServerFieldMap } from "@/shared/forms/serverErrors";

const NAME_MAX_LENGTH = 200;

/* The fields a project's details form shares with the new-project form. */
const details = {
  name: z.string().trim().min(2, "newProject.errors.name").max(NAME_MAX_LENGTH),
  service: z.enum(["web", "mobile", "automation", "marketing"]),
  client_id: z.string().transform((value) => (value === NO_CLIENT ? null : value)),
  starts_on: z.string().nullable(),
  due_on: z.string().nullable(),
  description: z.string().trim().max(DESCRIPTION_MAX_LENGTH),
};

function datesInOrder(values: { starts_on: string | null; due_on: string | null }): boolean {
  return !values.starts_on || !values.due_on || values.due_on >= values.starts_on;
}
const DATES_IN_ORDER = { path: ["due_on"], message: "newProject.errors.dueBeforeStart" };

export const newProjectSchema = z
  .object({
    ...details,
    lead_id: z.string().nullable(),
  })
  .refine(datesInOrder, DATES_IN_ORDER);
export type NewProjectInput = z.input<typeof newProjectSchema>;
export type NewProjectOutput = z.output<typeof newProjectSchema>;

export const projectDetailsSchema = z.object(details).refine(datesInOrder, DATES_IN_ORDER);
export type ProjectDetailsInput = z.input<typeof projectDetailsSchema>;
export type ProjectDetailsOutput = z.output<typeof projectDetailsSchema>;

/* The server names its fields like the forms do. */
const DETAILS_FIELDS = {
  name: "name",
  service: "service",
  client_id: "client_id",
  starts_on: "starts_on",
  due_on: "due_on",
  description: "description",
} as const;

export const PROJECT_DETAILS_SERVER_FIELDS: ServerFieldMap<ProjectDetailsInput> = DETAILS_FIELDS;

export const NEW_PROJECT_SERVER_FIELDS: ServerFieldMap<NewProjectInput> = {
  ...DETAILS_FIELDS,
  lead_id: "lead_id",
};
